import { Popconfirm, Radio, Select } from 'antd'
import { type FC } from 'react'

import { Alert, Button } from '@/components/elements'
import { SHIPMENT_ORDER_CSV_IMPORT } from '@/constants/common/message.constants'

import { type ImportMethod, type SavedFormat } from '../../models'

/** この画面では取り込まず、既存の取り込み（別モーダル）へ渡す選択肢。 */
export type ImportHandoffOption = {
  key: string
  title: string
  description: string
}

type Props = {
  method: ImportMethod | null
  onChangeMethod: (method: ImportMethod) => void
  handoffs: readonly ImportHandoffOption[]
  selectedHandoffKey: string | null
  onSelectHandoff: (key: string) => void
  savedFormats: readonly SavedFormat[]
  selectedFormatId: number | null
  onChangeFormatId: (id: number | null) => void
  /** 選んでいる保存フォーマットを削除する（この画面で確認を取ってから呼ぶ） */
  onDeleteFormat: () => void
}

type MethodOption = {
  value: ImportMethod
  title: string
  description: string
}

const HANDOFF_VALUE_PREFIX = 'handoff:'

const METHOD_OPTIONS: MethodOption[] = [
  {
    value: 'standard',
    title: SHIPMENT_ORDER_CSV_IMPORT.METHOD_STANDARD_TITLE,
    description: SHIPMENT_ORDER_CSV_IMPORT.METHOD_STANDARD_DESCRIPTION
  },
  {
    value: 'saved',
    title: SHIPMENT_ORDER_CSV_IMPORT.METHOD_SAVED_TITLE,
    description: SHIPMENT_ORDER_CSV_IMPORT.METHOD_SAVED_DESCRIPTION
  },
  {
    value: 'adhoc',
    title: SHIPMENT_ORDER_CSV_IMPORT.METHOD_ADHOC_TITLE,
    description: SHIPMENT_ORDER_CSV_IMPORT.METHOD_ADHOC_DESCRIPTION
  }
]

/**
 * ステップ0: 取り込み方法。配送依頼のインポートの入口はこの画面 1 つ。
 * - 通常フォーマット / 保存したフォーマット / フォーマット外 はこのモーダルで取り込む。
 * - handoffs（サントリー様の社内移動・得意先など）は選ぶと既存の取り込みモーダルへ渡す。
 * - 「保存したフォーマット」選択時のみ、保存済みフォーマットの Select を出す。
 * - saved / adhoc は同じマッピング画面に入る。saved は保存フォーマットを定義として当て、変えたら保存し直す。
 * - 保存フォーマットの削除は、選択欄の隣のボタンから確認を取って行う。
 */
export const MethodView: FC<Props> = ({
  method,
  onChangeMethod,
  handoffs,
  selectedHandoffKey,
  onSelectHandoff,
  savedFormats,
  selectedFormatId,
  onChangeFormatId,
  onDeleteFormat
}) => {
  const selectedFormatName =
    savedFormats.find((f) => f.id === selectedFormatId)?.name ?? null
  return (
    <div className="flex flex-col gap-3">
      <Alert title={SHIPMENT_ORDER_CSV_IMPORT.METHOD_ALERT_MESSAGE}>
        <span className="text-xs">
          {SHIPMENT_ORDER_CSV_IMPORT.METHOD_ALERT_DESCRIPTION}
        </span>
      </Alert>

      <Radio.Group
        className="flex w-full flex-col gap-2"
        value={
          selectedHandoffKey !== null
            ? `${HANDOFF_VALUE_PREFIX}${selectedHandoffKey}`
            : (method ?? undefined)
        }
        onChange={(e) => {
          const value = String(e.target.value)
          if (value.startsWith(HANDOFF_VALUE_PREFIX)) {
            onSelectHandoff(value.slice(HANDOFF_VALUE_PREFIX.length))
          } else {
            onChangeMethod(value as ImportMethod)
          }
        }}
        data-testid="shipment-order-csv-import__method"
      >
        {[
          ...METHOD_OPTIONS.map((option) => ({
            ...option,
            key: option.value as string,
            isSelected: selectedHandoffKey === null && method === option.value
          })),
          ...handoffs.map((handoff) => ({
            ...handoff,
            value: `${HANDOFF_VALUE_PREFIX}${handoff.key}`,
            key: `${HANDOFF_VALUE_PREFIX}${handoff.key}`,
            isSelected: selectedHandoffKey === handoff.key
          }))
        ].map((option) => {
          const isSelected = option.isSelected
          return (
            <Radio
              key={option.key}
              value={option.value}
              className={
                'm-0 w-full rounded border p-3 ' +
                (isSelected
                  ? 'border-primary bg-geek-blue-1'
                  : 'border-table-border')
              }
              data-testid={`shipment-order-csv-import__method-${option.value.replace(HANDOFF_VALUE_PREFIX, 'handoff-')}`}
            >
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-secondary">
                  {option.title}
                </span>
                <span className="text-xs text-gray-2">
                  {option.description}
                </span>
              </span>
            </Radio>
          )
        })}
      </Radio.Group>

      {selectedHandoffKey === null && method === 'saved' && (
        <div className="ml-6 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-medium text-secondary">
            {SHIPMENT_ORDER_CSV_IMPORT.METHOD_SAVED_SELECT_LABEL}
          </span>
          <Select<number>
            className="min-w-[280px]"
            placeholder={
              SHIPMENT_ORDER_CSV_IMPORT.METHOD_SAVED_SELECT_PLACEHOLDER
            }
            value={selectedFormatId ?? undefined}
            onChange={(v) => onChangeFormatId(v ?? null)}
            options={savedFormats.map((f) => ({ value: f.id, label: f.name }))}
            showSearch
            optionFilterProp="label"
            notFoundContent={
              SHIPMENT_ORDER_CSV_IMPORT.METHOD_SAVED_SELECT_EMPTY
            }
            data-testid="shipment-order-csv-import__saved-format-select"
          />
          <Popconfirm
            title={
              selectedFormatName
                ? SHIPMENT_ORDER_CSV_IMPORT.SAVED_FORMAT_DELETE_CONFIRM(
                    selectedFormatName
                  )
                : ''
            }
            disabled={selectedFormatName === null}
            onConfirm={onDeleteFormat}
            okButtonProps={{ danger: true }}
          >
            <Button
              size="small"
              danger
              disabled={selectedFormatName === null}
              data-testid="shipment-order-csv-import__saved-format-delete"
            >
              {SHIPMENT_ORDER_CSV_IMPORT.SAVED_FORMAT_DELETE}
            </Button>
          </Popconfirm>
        </div>
      )}
    </div>
  )
}
